import { useTranslation } from '@msdk/common'
import { FieldType, FormNode, GenericFieldValue, OperatorNames } from '@msdk/definitions'
import { ButtonIcon, CloseIcon, FormField, FormFieldLabel, MenuItem, styled, StyledSelect } from '@msdk/ui'
import React from 'react'

import { ChangeArgs } from '../../../models/form'
import { getFieldDefIncludeExternal } from '../../../utils'
import { ConditionalFieldRenderer } from '../../Fields'
import { useDynamicViewContext } from '../context'
import { getOperators } from './get-operators'
import { OPERATORS } from './operators'

const StyledFormField = styled(FormField)`
  min-width: auto;
  width: 156px;
`

const StyledButtonIcon = styled(ButtonIcon)`
  padding: 4px;
`

const Wrapper = styled('div')`
  padding: 8px 8px 8px 0;
  display: flex;
  flex-direction: row;
  column-gap: 8px;
  width: 100%;
`

const ValueWrapper = styled('div')`
  width: 156px;
`

interface Props {
  id: string
  column?: string
  operator?: string
  node?: FormNode
  onColumnChange: (id: string, column: string) => void
  onOperatorChange: (id: string, column: string, operator: OperatorNames) => void
  onValueChange: (id: string, column: string, operator: OperatorNames, value: GenericFieldValue) => void
  onDelete: (id: string) => void
}

export function Filter({
  id,
  onColumnChange,
  onDelete,
  onOperatorChange,
  onValueChange,
  column,
  node,
  operator,
}: Props) {
  const t = useTranslation()

  const { baseMetadata, filterColumns, viewModel } = useDynamicViewContext()

  const onColumnSelected = (e: Event) => {
    const columnName = (e.target as any).value
    onColumnChange(id, columnName)
  }

  const onOperatorSelected = (e: Event) => {
    const newOperator = (e.target as any).value
    onOperatorChange(id, column as OperatorNames, newOperator)
  }

  const getColumnOptions = () => {
    const columns = filterColumns
    return columns.map((c) => {
      const fieldDef = getFieldDefIncludeExternal(viewModel, c.fieldName, baseMetadata)
      const idml = c.overrideIDML ?? fieldDef.idml
      const description = c.overrideDescription ?? fieldDef.description
      return {
        value: c.fieldName,
        description,
        idml,
      }
    })
  }

  const getOperatorOptions = () => {
    if (column !== '') {
      const fieldDef = getFieldDefIncludeExternal(viewModel, column as string, baseMetadata)
      return getOperators(fieldDef.fieldDefinitionType)
    }
    return []
  }

  const onValueChanged = (args: ChangeArgs) => {
    onValueChange(id, column as string, operator as OperatorNames, args.value)
  }

  const onDelte = () => {
    onDelete(id)
  }

  return (
    <Wrapper>
      <StyledButtonIcon onClick={onDelte}>
        <CloseIcon />
      </StyledButtonIcon>
      <StyledFormField size="small">
        <FormFieldLabel>{t('LBL_FILTER_COLUMNS', 'Columns')} </FormFieldLabel>
        <StyledSelect value={column} label={t('LBL_FILTER_COLUMNS', 'Columns')} onChange={onColumnSelected}>
          {getColumnOptions().map((c) => (
            <MenuItem key={c.value} value={c.value}>
              {t(c.idml, c.description)}
            </MenuItem>
          ))}
        </StyledSelect>
      </StyledFormField>
      <StyledFormField size="small">
        <FormFieldLabel>{t('LBL_FILTER_OPERATOR', 'Operator')}</FormFieldLabel>
        <StyledSelect value={operator} label={t('LBL_FILTER_OPERATOR', 'Operator')} onChange={onOperatorSelected}>
          {getOperatorOptions().map((c) => (
            <MenuItem key={c.value} value={c.value}>
              {t(c.idml, c.description)}
            </MenuItem>
          ))}
        </StyledSelect>
      </StyledFormField>
      {operator && OPERATORS[operator as OperatorNames].fieldVisible && (
        <ValueWrapper>
          <ConditionalFieldRenderer
            node={node as FormNode}
            onChange={onValueChanged}
            type={node?.value.type as FieldType}
          />
        </ValueWrapper>
      )}
    </Wrapper>
  )
}
